import { readJson, writeJson } from "./local-store.ts"

/**
 * 자주 보는 조건에 이름을 붙여 둔다("미입고만", "이번 달 폐기").
 *
 * 이 화면의 조건은 전부 URL 에 있으므로(`lib/case-filters.ts`) 저장할 것도 **주소 한 줄**이다.
 * 필터마다 따로 담으면 새 필터가 생길 때마다 저장 형식을 고쳐야 하고, 옛 뷰는 그 필터를 잃는다.
 */
export type SavedView = { name: string; query: string }

/** 뷰에 담지 않는 열쇠 — 옆판(detail·tab)은 화면 상태이고, 쪽수는 조건이 아니다. */
const IGNORED = ["detail", "tab", "page"]

/** 열쇠 순서가 달라도 같은 조건이면 같은 글자가 되게 맞춘다. */
export function normalizeQuery(search: string): string {
  const params = new URLSearchParams(search)
  for (const key of IGNORED) params.delete(key)
  const pairs = [...params.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return new URLSearchParams(pairs).toString()
}

/** 한 계정이 쌓아 둘 수 있는 뷰. 이 이상은 칩이 화면을 덮는다. */
export const MAX_VIEWS = 12

export function addView(views: readonly SavedView[], name: string, search: string): SavedView[] {
  const trimmed = name.trim()
  if (trimmed === "") return [...views]
  const query = normalizeQuery(search)
  // 같은 이름은 덮어쓴다 — 조건을 고쳐 다시 저장하는 것이 흔한 일이다.
  const rest = views.filter((view) => view.name !== trimmed)
  return [...rest, { name: trimmed, query }].slice(-MAX_VIEWS)
}

export function removeView(views: readonly SavedView[], name: string): SavedView[] {
  return views.filter((view) => view.name !== name)
}

/** 지금 보고 있는 조건과 같은 뷰. 칩을 켜 두어 "지금 이 뷰"임을 보인다. */
export function activeView(views: readonly SavedView[], search: string): SavedView | null {
  const now = normalizeQuery(search)
  return views.find((view) => view.query === now) ?? null
}

/** 조건이 하나도 없으면 저장할 것이 없다(전체 목록에 이름을 붙일 이유가 없다). */
export function canSave(search: string): boolean {
  return normalizeQuery(search) !== ""
}

export function loadViews(key: string): SavedView[] {
  const raw = readJson<unknown>(key, [])
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (item): item is SavedView =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as SavedView).name === "string" &&
      typeof (item as SavedView).query === "string",
  )
}

export function saveViews(key: string, views: readonly SavedView[]): void {
  writeJson(key, views)
}
